import { Check, X } from '@/components/icons'
import { Card } from './ui/card'
import { Textarea } from './ui/textarea'
import { prntrTextareaClass } from './ui/prntr-input'
import { cn } from '@/lib/utils'
import { FieldError } from './FieldError'

// Single-line editor matching the PRNTR textarea field box (label sits above
// in the FieldCard, so the row layout doesn't apply). 16px — iOS zoom rule.
const prntrBoxInputClass =
  'h-11 w-full rounded-[10px] border border-border-L1 bg-surface-L2 px-3 text-base font-semibold text-content-primary-bold placeholder:text-content-primary-subtle-mid focus:outline-none'

interface FieldCardProps {
  label: string
  isRequired?: boolean
  error?: string
  footer?: React.ReactNode
  children: React.ReactNode
}

function FieldCard({ label, isRequired, error, footer, children }: FieldCardProps) {
  return (
    <Card className={cn('flex flex-col gap-2', error ? 'border-border-negative-bold' : 'border-border-L1')}>
      <div className="flex items-center gap-1">
        <span className="text-sm font-semibold text-content-primary-bold leading-5 whitespace-pre-wrap">{label}</span>
        {isRequired && <span className="text-xs text-content-negative-bold">*</span>}
      </div>
      {children}
      {footer}
      <FieldError message={error} />
    </Card>
  )
}

interface BaseFieldProps {
  label: string
  isRequired?: boolean
  readOnly?: boolean
  error?: string
  footer?: React.ReactNode
}

export interface YesNoFieldProps extends BaseFieldProps {
  value: boolean | null
  onCommit?: (value: boolean | null) => void
}

export function YesNoField({
  label,
  isRequired,
  readOnly,
  error,
  footer,
  value,
  onCommit,
}: YesNoFieldProps) {
  const isReadOnly = readOnly || !onCommit

  const handleSelect = (next: boolean) => {
    if (isReadOnly) return
    onCommit?.(value === next ? null : next)
  }

  return (
    <FieldCard label={label} isRequired={isRequired} error={error} footer={footer}>
      <div className="flex gap-2">
        <YesNoOption
          selected={value === true}
          tone="positive"
          icon={<Check className="size-3.5" />}
          label="Yes"
          readOnly={isReadOnly}
          onClick={() => handleSelect(true)}
        />
        <YesNoOption
          selected={value === false}
          tone="destructive"
          icon={<X className="size-3.5" />}
          label="No"
          readOnly={isReadOnly}
          onClick={() => handleSelect(false)}
        />
      </div>
    </FieldCard>
  )
}

function YesNoOption({
  selected,
  tone,
  icon,
  label,
  readOnly,
  onClick,
}: {
  selected: boolean
  tone: 'positive' | 'destructive'
  icon: React.ReactNode
  label: string
  readOnly: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={onClick}
      className={cn(
        'flex-1 flex items-center justify-center gap-2 min-h-9 px-4 py-[7.5px] rounded-lg text-sm font-semibold transition-colors',
        selected && tone === 'positive' && 'bg-background-positive-subtle text-content-positive-default',
        selected && tone === 'destructive' && 'bg-background-negative-subtle text-content-negative-bold',
        !selected && 'bg-surface-L2 text-content-primary-subtle',
        !readOnly && 'cursor-pointer',
        readOnly && 'cursor-default'
      )}
    >
      {icon}
      {label}
    </button>
  )
}

export interface TextFieldProps extends BaseFieldProps {
  value: string | null
  onCommit?: (value: string | null) => void
  placeholder?: string
}

export function TextField({
  label,
  isRequired,
  readOnly,
  error,
  footer,
  value,
  onCommit,
  placeholder,
}: TextFieldProps) {
  const isReadOnly = readOnly || !onCommit

  return (
    <FieldCard label={label} isRequired={isRequired} error={error} footer={footer}>
      <input
        type="text"
        value={value ?? ''}
        readOnly={isReadOnly}
        onChange={(e) => {
          const next = e.target.value
          onCommit?.(next.length ? next : null)
        }}
        placeholder={placeholder ?? (isReadOnly ? 'No answer' : 'Enter answer')}
        className={cn(prntrBoxInputClass, isReadOnly && 'pointer-events-none')}
      />
    </FieldCard>
  )
}

export interface TextAreaFieldProps extends BaseFieldProps {
  value: string | null
  onCommit?: (value: string | null) => void
  placeholder?: string
  rows?: number
}

export function TextAreaField({
  label,
  isRequired,
  readOnly,
  error,
  footer,
  value,
  onCommit,
  placeholder,
  rows = 3,
}: TextAreaFieldProps) {
  const isReadOnly = readOnly || !onCommit

  return (
    <FieldCard label={label} isRequired={isRequired} error={error} footer={footer}>
      <Textarea
        value={value ?? ''}
        readOnly={isReadOnly}
        onChange={(e) => {
          const next = e.target.value
          onCommit?.(next.length ? next : null)
        }}
        placeholder={placeholder ?? (isReadOnly ? 'No answer' : 'Enter answer')}
        rows={rows}
        className={cn(prntrTextareaClass, isReadOnly && 'pointer-events-none')}
      />
    </FieldCard>
  )
}

export interface NumberFieldProps extends BaseFieldProps {
  value: number | null
  onCommit?: (value: number | null) => void
  placeholder?: string
}

export function NumberField({
  label,
  isRequired,
  readOnly,
  error,
  footer,
  value,
  onCommit,
  placeholder,
}: NumberFieldProps) {
  const isReadOnly = readOnly || !onCommit

  return (
    <FieldCard label={label} isRequired={isRequired} error={error} footer={footer}>
      <input
        type="number"
        inputMode="decimal"
        value={value === null ? '' : String(value)}
        readOnly={isReadOnly}
        onChange={(e) => {
          const raw = e.target.value
          if (raw.trim() === '') {
            onCommit?.(null)
            return
          }
          const parsed = Number(raw)
          if (!Number.isNaN(parsed)) onCommit?.(parsed)
        }}
        placeholder={placeholder ?? (isReadOnly ? 'No answer' : 'Enter number')}
        className={cn(prntrBoxInputClass, isReadOnly && 'pointer-events-none')}
      />
    </FieldCard>
  )
}
